import React, { useEffect, useState } from 'react'
import * as R from 'ramda'
import { Dropdown, Form } from 'semantic-ui-react'

/**
 * @typedef ScoreInputDropdownProps
 * @property {string=} data-property
 * @property {string} defaultValue
 * @property {function} onChange
 *
 * @param {ScoreInputDropdownProps} props
 * @returns
 */
export default function ScoreIconDropdown({
  'data-property': dataProperty,
  defaultValue,
  onChange,
}) {
  const [selectedIcon, setSelectedIcon] = useState(null)

  function onSelect(data) {
    setSelectedIcon(data)
    onChange(data.value)
  }

  useEffect(() => {
    if (defaultValue) {
      const options = [
        {
          key: 'hidden',
          text: 'No icon',
          value: 'hidden',
        },
        ...SIMPLESAT_ICON_OPTIONS,
        ...EMOJI_OPTIONS,
      ]

      const defaultIcon = R.find(R.propEq('value', defaultValue))(options)
      setSelectedIcon(defaultIcon)
    }
  }, [defaultValue, setSelectedIcon])

  if (!selectedIcon) {
    return <Form.Select label="Icon" fluid options={[]} />
  }

  return (
    <Form.Field>
      <Dropdown
        data-property={dataProperty}
        selection
        text={<IconText selectedIcon={selectedIcon} />}
      >
        <Dropdown.Menu>
          <Dropdown.Item
            key="hidden"
            text={
              <span>
                <i className="icon fas fa-eye-slash" />
                <span className="ml-1">Hide</span>
              </span>
            }
            value="hidden"
            onClick={(_, data) => onSelect(data)}
            active={selectedIcon.value === 'hidden'}
            selected={selectedIcon.value === 'hidden'}
          />
          <Dropdown.Header content="Simplesat icons" className="!my-2 !normal-case !text-base" />
          <div className="px-4 flex flex-wrap bg-white">
            {SIMPLESAT_ICON_OPTIONS.map((icon) => (
              <Dropdown.Item
                className="p-2 cursor-pointer hover:bg-grey-lighter rounded-md"
                key={icon.value}
                value={icon.value}
                text={
                  <img
                    width="24"
                    height="24"
                    className="ui image"
                    src={icon.image}
                    alt={icon.name}
                  />
                }
                onClick={() => onSelect(icon)}
                active={selectedIcon.value === icon.value}
                selected={selectedIcon.value === icon.value}
              />
            ))}
          </div>
          <Dropdown.Header content="Emojis" className="!my-2 !normal-case !text-base" />
          <div className="px-4 flex flex-wrap bg-white pb-2">
            {EMOJI_OPTIONS.map((emoji) => (
              <Dropdown.Item
                className="px-2 py-1 cursor-pointer inline-block text-2xl hover:bg-grey-lighter rounded-md"
                key={emoji.value}
                text={`${emoji.text} `}
                value={emoji.value}
                onClick={() => onSelect(emoji)}
                active={selectedIcon.value === emoji.value}
                selected={selectedIcon.value === emoji.value}
              />
            ))}
          </div>
        </Dropdown.Menu>
      </Dropdown>
    </Form.Field>
  )
}
/**
 * @typedef {object} selectedIcon
 * @property {string} type
 * @property {string} name
 * @property {string=} text
 * @property {string=} name
 *
 * @param {object} props
 * @param {object} props.selectedIcon

 * @returns
 */
function IconText({ selectedIcon }) {
  if (selectedIcon.type === 'image') {
    return (
      <span className="!flex !items-center">
        <img className="ui image !m-0 !w-4" src={selectedIcon.image} alt={selectedIcon.name} />
        <span className="ml-2">{selectedIcon.name}</span>
      </span>
    )
  } else if (selectedIcon.type === 'emoji') {
    return (
      <span className="!flex !items-center">
        {selectedIcon.text}
        <span className="ml-2">{selectedIcon.name}</span>
      </span>
    )
  } else {
    return selectedIcon.text
  }
}

const SIMPLESAT_ICON_OPTIONS = [
  {
    name: 'Simplesat smiley face good',
    value: 'simplesat_smiley_face',
    type: 'image',
    image: 'https://cdn.simplesat.io/choice_images/defaults/smiley-4.png?size=24x24',
  },
  {
    name: 'Simplesat smiley face great',
    value: 'simplesat_smiley_face_great',
    type: 'image',
    image: 'https://cdn.simplesat.io/choice_images/defaults/smiley-5.png?size=24x24',
  },
  {
    name: 'Smilesat smiley face 2 good',
    value: 'simplesat_smiley_face_2_good',
    type: 'image',
    image: 'https://cdn.simplesat.io/choice_images/defaults/smiley2-4.5300.png?size=24x24',
  },
  {
    name: 'Smilesat smiley face 2 good (gif)',
    value: 'simplesat_smiley_face_2_good_gif',
    type: 'image',
    image: 'https://cdn.simplesat.io/choice_images/defaults/smiley2-4.5300.gif',
  },
  {
    name: 'Smilesat smiley face 2 great',
    value: 'simplesat_smiley_face_2_great',
    type: 'image',
    image: 'https://cdn.simplesat.io/choice_images/defaults/smiley2-5.5300.png?size=24x24',
  },
  {
    name: 'Smilesat smiley face 2 great (gif)',
    value: 'simplesat_smiley_face_2_great_gif',
    type: 'image',
    image: 'https://cdn.simplesat.io/choice_images/defaults/smiley2-5.5300.gif',
  },
  {
    name: 'Simplesat hand good',
    value: 'simplesat_hand',
    type: 'image',
    image: 'https://cdn.simplesat.io/choice_images/defaults/hand-4.5300.png?size=24x24',
  },
  {
    name: 'Simplesat hand good (gif)',
    value: 'simplesat_hand_gif',
    type: 'image',
    image: 'https://cdn.simplesat.io/choice_images/defaults/hand-4.5300.gif',
  },
  {
    name: 'Simplesat hand great',
    value: 'simplesat_hand_great',
    type: 'image',
    image: 'https://cdn.simplesat.io/choice_images/defaults/hand-5.5300.png?size=24x24',
  },
  {
    name: 'Simplesat hand great (gif)',
    value: 'simplesat_hand_great_gif',
    type: 'image',
    image: 'https://cdn.simplesat.io/choice_images/defaults/hand-5.5300.gif',
  },
  {
    name: 'Simplecat good',
    value: 'simplesat_cat',
    type: 'image',
    image: 'https://cdn.simplesat.io/choice_images/defaults/cat-4.5300.png?size=24x24',
  },
  {
    name: 'Simplecat good (gif)',
    value: 'simplesat_cat_gif',
    type: 'image',
    image: 'https://cdn.simplesat.io/choice_images/defaults/cat-4.5300.gif',
  },
  {
    name: 'Simplecat great',
    value: 'simplecat_great',
    type: 'image',
    image: 'https://cdn.simplesat.io/choice_images/defaults/cat-5.5300.png?size=24x24',
  },
  {
    name: 'Simplecat great (gif)',
    value: 'simplecat_great_gif',
    type: 'image',
    image: 'https://cdn.simplesat.io/choice_images/defaults/cat-5.5300.gif',
  },
  {
    name: 'Simplesat cloud good',
    value: 'simplesat_cloud_good',
    type: 'image',
    image: 'https://cdn.simplesat.io/choice_images/defaults/cloud-4.5300.png?size=24x24',
  },
  {
    name: 'Simplesat cloud good (gif)',
    value: 'simplesat_cloud_good_gif',
    type: 'image',
    image: 'https://cdn.simplesat.io/choice_images/defaults/cloud-4.5300.gif',
  },
  {
    name: 'Simplesat cloud great',
    value: 'simplesat_cloud_great',
    type: 'image',
    image: 'https://cdn.simplesat.io/choice_images/defaults/cloud-5.5300.png?size=24x24',
  },
  {
    name: 'Simplesat cloud great (gif)',
    value: 'simplesat_cloud_great_gif',
    type: 'image',
    image: 'https://cdn.simplesat.io/choice_images/defaults/cloud-5.5300.gif',
  },
]

const EMOJI_OPTIONS = [
  {
    name: 'Grinning',
    value: 'grinning',
    text: '😀',
    type: 'emoji',
  },
  {
    name: 'Smile',
    value: 'smile',
    text: '😄',
    type: 'emoji',
  },
  {
    name: 'Wink',
    value: 'wink',
    text: '😉',
    type: 'emoji',
  },
  {
    name: 'Blush',
    value: 'blush',
    text: '😊',
    type: 'emoji',
  },
  {
    name: 'Heart eyes',
    value: 'heart_eyes',
    text: '😍',
    type: 'emoji',
  },
  {
    name: 'Star struck',
    value: 'star_struck',
    text: '🤩',
    type: 'emoji',
  },
  {
    name: 'Smiling face with hearts',
    value: 'smiling_face_with_hearts',
    text: '🥰',
    type: 'emoji',
  },
  {
    name: 'Smiling face with sunglasses',
    value: 'smiling_face_with_sunglasses',
    text: '😎',
    type: 'emoji',
  },
  {
    name: 'Partying face',
    value: 'partying_face',
    text: '🥳',
    type: 'emoji',
  },
  {
    name: 'Point right',
    value: 'point_right',
    text: '👉',
    type: 'emoji',
  },
  {
    name: 'Victory',
    value: 'victory',
    text: '✌️',
    type: 'emoji',
  },
  {
    name: 'I love you',
    value: 'i_love_you',
    text: '🤟',
    type: 'emoji',
  },
  {
    name: 'Star',
    value: 'star',
    text: '⭐️',
    type: 'emoji',
  },
  {
    name: 'Glowing star',
    value: 'glowing_star',
    text: '🌟',
    type: 'emoji',
  },
  {
    name: 'Sparkles',
    value: 'sparkles',
    text: '✨',
    type: 'emoji',
  },
  {
    name: 'Rainbow',
    value: 'rainbow',
    text: '🌈',
    type: 'emoji',
  },
  {
    name: '1st place medal',
    value: 'first_place_medal',
    text: '🥇',
    type: 'emoji',
  },
  {
    name: 'Sports medal',
    value: 'sports_medal',
    text: '🏅',
    type: 'emoji',
  },
  {
    name: 'Party popper',
    value: 'party_popper',
    text: '🎉',
    type: 'emoji',
  },
  {
    name: 'Red heart',
    value: 'red_heart',
    text: '❤️',
    type: 'emoji',
  },
  {
    name: 'Blue heart',
    value: 'blue_heart',
    text: '💙',
    type: 'emoji',
  },
  {
    name: 'Green heart',
    value: 'green_heart',
    text: '💚',
    type: 'emoji',
  },
  {
    name: 'Yellow heart',
    value: 'yellow_heart',
    text: '💛',
    type: 'emoji',
  },
  {
    name: 'Purple heart',
    value: 'purple_heart',
    text: '💜',
    type: 'emoji',
  },
  {
    name: 'Orange heart',
    value: 'orange_heart',
    text: '🧡',
    type: 'emoji',
  },
  {
    name: 'Two hearts',
    value: 'two_hearts',
    text: '💕',
    type: 'emoji',
  },
  {
    name: 'Revolving hearts',
    value: 'revolving_hearts',
    text: '💞',
    type: 'emoji',
  },
  {
    name: 'Beating heart',
    value: 'beating_heart',
    text: '💓',
    type: 'emoji',
  },
  {
    name: 'Growing heart',
    value: 'growing_heart',
    text: '💗',
    type: 'emoji',
  },
  {
    name: 'Sparkling hear',
    value: 'sparkling_hear',
    text: '💖',
    type: 'emoji',
  },
]



// WEBPACK FOOTER //
// ./node_modules/eslint-loader??ref--0-0!./src/components/ScoreIconDropdown/index.js